import { CheckableField, CheckableFields, FormField, Heading, TextInput } from '@msaf/core-react'
import { TOOLTIP } from '../../../../constants/tooltips'
import { InputWidth } from '../../../../types/aoraki/input-width-types'
import { FormFieldsEdit } from '../../../../types/form-types'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import LookupField from '../../wells/lookup-field'

interface WellHeadSecurityDetailsProps {
  isLoading: boolean
  fields: Partial<FormFieldsEdit>
  validationErrors: ValidationErrors
  setValue: SetValue<FormFieldsEdit>
}

export default function EditWellHeadSecurityConstruction({
  isLoading,
  fields,
  setValue,
  validationErrors,
}: WellHeadSecurityDetailsProps) {
  return (
    <>
      <Heading isSkeleton={isLoading} level={3}>
        Wellhead construction
      </Heading>

      <FormField
        validationMessages={validationErrors?.wellCap}
        isSkeleton={isLoading}
        labelId='wellCapLabel'
        label='Well cap'
        toolTipMessage={TOOLTIP.wellCap}
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy={'wellCapLabel'}
          fieldId='wellCap'
          value={`${fields.wellCap ?? ''}`}
          setValue={({ value }) => setValue('wellCap', value)}
          isInvalid={!!validationErrors.wellCap}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.sealAroundWellhead}
        isSkeleton={isLoading}
        labelId='sealAroundWellheadLabel'
        label='Seal around wellhead'
        toolTipMessage={TOOLTIP.sealAroundWellhead}
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy={'sealAroundWellheadLabel'}
          fieldId='sealAroundWellhead'
          value={`${fields.sealAroundWellhead ?? ''}`}
          setValue={({ value }) => setValue('sealAroundWellhead', value)}
          isInvalid={!!validationErrors.sealAroundWellhead}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.concretePad}
        isSkeleton={isLoading}
        labelId='concretePadLabel'
        label='Concrete pad'
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy={'concretePadLabel'}
          fieldId='concretePad'
          value={`${fields.concretePad ?? ''}`}
          setValue={({ value }) => setValue('concretePad', value)}
          isInvalid={!!validationErrors.concretePad}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.bungsSealed}
        isSkeleton={isLoading}
        labelId='bungsSealedLabel'
        label='Bungs / ports securely sealed'
        toolTipMessage={TOOLTIP.bungsSealed}
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy={'bungsSealedLabel'}
          fieldId='bungsSealed'
          value={`${fields.bungsSealed ?? ''}`}
          setValue={({ value }) => setValue('bungsSealed', value)}
          isInvalid={!!validationErrors.bungsSealed}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.isWellUpstand03M}
        isSkeleton={isLoading}
        labelId='isWellUpstand03MLabel'
        label='Upstand 0.3m or greater above ground level'
      >
        <CheckableFields ariaLabelledBy='isWellUpstand03MLabel' isHorizontal>
          <CheckableField
            id='isWellUpstand03MYes'
            name='isWellUpstand03M'
            describedBy='isWellUpstand03MLabel'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.isWellUpstand03M ?? false}
            onChange={() => setValue('isWellUpstand03M', true)}
          />
          <CheckableField
            id='isWellUpstand03mNo'
            name='isWellUpstand03M'
            describedBy='isWellUpstand03MLabel'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.isWellUpstand03M === false}
            onChange={() => setValue('isWellUpstand03M', false)}
          />
        </CheckableFields>
      </FormField>

      <FormField
        validationMessages={validationErrors?.isWellInstalledInFrostPit}
        isSkeleton={isLoading}
        labelId='isWellInstalledInFrostPitLabel'
        label='Installed in frost pit'
      >
        <CheckableFields ariaLabelledBy='isWellInstalledInFrostPitLabel' isHorizontal>
          <CheckableField
            id='isWellInstalledInFrostPitYes'
            name='isWellInstalledInFrostPit'
            describedBy='isWellInstalledInFrostPitLabel'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.isWellInstalledInFrostPit ?? false}
            onChange={() => setValue('isWellInstalledInFrostPit', true)}
          />
          <CheckableField
            id='isWellInstalledInFrostPitNo'
            name='isWellInstalledInFrostPit'
            describedBy='isWellInstalledInFrostPitLabel'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.isWellInstalledInFrostPit === false}
            onChange={() => setValue('isWellInstalledInFrostPit', false)}
          />
        </CheckableFields>
      </FormField>

      <FormField
        validationMessages={validationErrors?.accessForWaterLevelMeasurement}
        isSkeleton={isLoading}
        labelId='accessForWaterLevelMeasurementLabel'
        label='Groundwater level access'
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy={'accessForWaterLevelMeasurementLabel'}
          fieldId='groundwaterLevelAccess'
          value={`${fields.accessForWaterLevelMeasurement ?? ''}`}
          setValue={({ value }) => setValue('accessForWaterLevelMeasurement', value)}
          isInvalid={!!validationErrors.accessForWaterLevelMeasurement}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.samplingTapLocation}
        isSkeleton={isLoading}
        htmlFor='samplingTapLocation'
        label='Sample point description'
        toolTipMessage={TOOLTIP.samplingTapLocation}
      >
        <TextInput
          id={'samplingTapLocation'}
          isSkeleton={isLoading}
          value={`${fields?.samplingTapLocation ?? ''}`}
          setValue={(val: string) => setValue('samplingTapLocation', val)}
          isInvalid={!!validationErrors.samplingTapLocation}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.pumpType}
        isSkeleton={isLoading}
        labelId='pumpTypeLabel'
        label='Pump type'
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy='pumpTypeLabel'
          fieldId='pumpType'
          value={`${fields.pumpType ?? ''}`}
          setValue={({ value }) => setValue('pumpType', value)}
          isInvalid={!!validationErrors.pumpType}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.pumpInstallDepth}
        isSkeleton={isLoading}
        htmlFor='pumpInstallDepth'
        label='Pump install depth'
        toolTipMessage={TOOLTIP.pumpInstallDepth}
      >
        <TextInput
          fieldSize={InputWidth.medium}
          id='pumpInstallDepth'
          isSkeleton={isLoading}
          value={`${fields?.pumpInstallDepth ?? ''}`}
          setValue={(val: string) => setValue('pumpInstallDepth', val)}
          isInvalid={!!validationErrors.pumpInstallDepth}
        />
      </FormField>

      <FormField
        validationMessages={validationErrors?.backflowPrevention}
        isSkeleton={isLoading}
        labelId='backflowPreventionLabel'
        label='Backflow prevention'
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy='backflowPreventionLabel'
          fieldId='backflowPrevention'
          value={`${fields.backflowPrevention ?? ''}`}
          setValue={({ value }) => setValue('backflowPrevention', value)}
          isInvalid={!!validationErrors.backflowPrevention}
        />
      </FormField>
    </>
  )
}
